import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Movie } from "@/mocks/movies";

interface HeroCarouselProps {
  slides: Movie[];
}

export default function HeroCarousel({ slides }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setIndex((i) => (i + 1) % slides.length);
    }, 5500);
    return () => clearInterval(timer);
  }, [slides.length]);

  if (slides.length === 0) return null;

  const go = (dir: number) => {
    setIndex((i) => (i + dir + slides.length) % slides.length);
  };

  return (
    <div className="relative h-[420px] w-full overflow-hidden rounded-xl bg-primary-900 md:h-[520px]">
      {slides.map((slide, i) => (
        <Link
          key={slide.id}
          to={`/detail/${slide.id}`}
          className={`absolute inset-0 cursor-pointer transition-opacity duration-700 ${
            i === index ? "opacity-100" : "pointer-events-none opacity-0"
          }`}
        >
          <img
            src={slide.backdrop}
            alt={`${slide.title} 剧照`}
            title={`${slide.title} - 秋霞影院精选影视`}
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950/90 via-primary-950/55 to-primary-950/10" />
          <div className="absolute inset-x-0 bottom-0 p-6 md:p-10">
            <span className="inline-flex items-center gap-1 rounded-full bg-accent-500 px-3 py-1 text-xs font-semibold text-white">
              <i className="ri-fire-fill" /> 编辑精选
            </span>
            <h2 className="mt-3 font-heading text-2xl font-bold text-white md:text-4xl">{slide.title}</h2>
            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-background-200 md:text-sm">
              <span className="flex items-center gap-1 font-semibold text-accent-400">
                <i className="ri-star-fill" /> {slide.rating.toFixed(1)}
              </span>
              <span>{slide.year}</span>
              <span>{slide.region}</span>
              <span>{slide.genre.join(" / ")}</span>
              <span>{slide.duration}</span>
            </div>
            <p className="clamp-2 mt-3 hidden max-w-2xl text-sm leading-relaxed text-background-200 md:block">
              {slide.synopsis}
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <span className="flex items-center gap-1.5 rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-accent-600">
                <i className="ri-play-fill text-base" /> 观看入口
              </span>
              <span className="flex items-center gap-1.5 rounded-md border border-background-100/40 px-5 py-2.5 text-sm font-medium text-white">
                <i className="ri-information-line" /> 查看详情
              </span>
            </div>
          </div>
        </Link>
      ))}

      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          go(-1);
        }}
        className="absolute left-3 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-primary-950/50 text-white transition-colors hover:bg-accent-500 md:left-5 md:h-10 md:w-10"
        aria-label="上一张"
      >
        <i className="ri-arrow-left-s-line text-xl" />
      </button>
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          go(1);
        }}
        className="absolute right-3 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-primary-950/50 text-white transition-colors hover:bg-accent-500 md:right-5 md:h-10 md:w-10"
        aria-label="下一张"
      >
        <i className="ri-arrow-right-s-line text-xl" />
      </button>

      <div className="absolute bottom-4 right-5 z-10 flex items-center gap-1.5">
        {slides.map((slide, i) => (
          <button
            key={slide.id}
            type="button"
            onClick={(e) => {
              e.preventDefault();
              setIndex(i);
            }}
            className={`h-1.5 cursor-pointer rounded-full transition-all ${
              i === index ? "w-6 bg-accent-500" : "w-1.5 bg-background-100/60"
            }`}
            aria-label={`切换到第 ${i + 1} 张`}
          />
        ))}
      </div>
    </div>
  );
}